import Typography from "components/basic/typography";
import { useTranslation } from "react-i18next";
import { useFormat } from "hooks/useFormat";
import { GhgData } from "../calculatorFormulas";
import { useCallback, useMemo } from "react";
import { useTheme } from "@mui/material";

interface GhgInfographicProps {
  ghgData: GhgData;
  height?: number;
  width?: number;
}

const MAX_SIZE = 115; // Maximum size for the infographic circles

const GhgInfographic: React.FC<GhgInfographicProps> = ({
  ghgData: data,
  height = 100,
  width = 540,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("calculator");
  const format = useFormat();

  const calculateSizes = useCallback(
    (
      evSize: number,
      iceSize: number,
      MAX_SIZE: number,
    ): [ev: number, ice: number] => {
      if (evSize === 0 && iceSize === 0) {
        return [0, 0];
      }

      if (evSize >= iceSize) {
        const scale = iceSize / evSize;
        return [MAX_SIZE, MAX_SIZE * scale];
      } else {
        const scale = evSize / iceSize;
        return [MAX_SIZE * scale, MAX_SIZE];
      }
    },
    [],
  );

  const evHeight = useMemo(
    () =>
      calculateSizes(
        data.evCarbonFootprint,
        data.iceCarbonFootprint,
        MAX_SIZE,
      )[0],
    [calculateSizes, data.evCarbonFootprint, data.iceCarbonFootprint],
  );

  const iceHeight = useMemo(
    () =>
      calculateSizes(
        data.evCarbonFootprint,
        data.iceCarbonFootprint,
        MAX_SIZE,
      )[1],
    [calculateSizes, data.evCarbonFootprint, data.iceCarbonFootprint],
  );

  const evWidth = useMemo(
    () =>
      calculateSizes(
        data.evCarbonFootprint,
        data.iceCarbonFootprint,
        MAX_SIZE,
      )[0],
    [calculateSizes, data.evCarbonFootprint, data.iceCarbonFootprint],
  );

  const iceWidth = useMemo(
    () =>
      calculateSizes(
        data.evCarbonFootprint,
        data.iceCarbonFootprint,
        MAX_SIZE,
      )[1],
    [calculateSizes, data.evCarbonFootprint, data.iceCarbonFootprint],
  );

  const number = 5;

  return (
    <div
      style={{
        display: "flex",
        height: height,
        width: width,
        alignContent: "center",
        justifyContent: "space-between",
      }}
    >
      <div style={{ display: "flex" }}>
        <div
          style={{
            display: "flex",
            alignItems: "top ",
            gap: theme.spacing(0.5),
            paddingTop: theme.spacing(2),
          }}
        >
          <div
            style={{
              height: `${evHeight}px`,
              width: `${evWidth}px`,
              borderRadius: "50%",
              backgroundColor: "#F9A21B",
            }}
          />

          <div
            style={{
              height: "1px",
              width: "40px",
              backgroundColor: theme.palette.common.black,
            }}
          />
        </div>

        <div>
          <Typography variant="h4">
            {t("ghg_ev", { kg: format.number(Math.round(number)) })}
          </Typography>

          {data.evComparisonText.map((text, _) => (
            <Typography
              key={text}
              variant="body2"
              style={{
                color: theme.palette.text.secondary,
                fontStyle: "italic",
              }}
            >
              {text}
            </Typography>
          ))}
        </div>
      </div>

      <div style={{ display: "flex" }}>
        <div
          style={{
            display: "flex",
            alignItems: "top ",
          }}
        >
          <div
            style={{
              height: `${iceHeight}px`,
              width: `${iceWidth}px`,
              borderRadius: "50%",
              backgroundColor: "#111F3B",
            }}
          />

          <div
            style={{
              marginTop: theme.spacing(2),
              height: "1px",
              width: "40px",
              backgroundColor: theme.palette.common.black,
              marginLeft: theme.spacing(-1.5),
            }}
          />
        </div>

        <div>
          <Typography variant="h4">
            {t("ghg_ice", {
              kg: format.number(Math.round(data.iceCarbonFootprint)),
            })}
          </Typography>

          {data.iceComparisonText.map((text) => (
            <Typography
              key={text}
              variant="body2"
              style={{
                color: theme.palette.text.secondary,
                fontStyle: "italic",
              }}
            >
              {text}
            </Typography>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GhgInfographic;
